import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useCallback, useEffect, useMemo, useState, ChangeEvent } from "react";
import { ModalConfirm } from "../../../components/common/ModalConfirm/ModalConfirm";
import { CustomSnackbar } from "../../../components/common/CustomSnackbar/CustomSnackbar";
import { CustomBackdrop } from "../../../components/common/CustomBackdrop/CustomBackdrop";
import { getWatanaBatchStatus } from "./watanaBatchStatus";

export type WatanaBatchSummary = {
  batch_id: number;
  batch_code: string;
  status: string;
  status_label: string;
  total_certificates: number;
  processed_certificates: number;
  requested_at?: string;
  finished_at?: string;
  cancelled_at?: string;
  error_message?: string;
  has_sent_items?: boolean;
  can_cancel: boolean;
  cancel_block_reason?: string | null;
};

type WatanaBatchesDrawerProps = {
  open: boolean;
  onClose: () => void;
  idcycle: number;
  fetchBatches: (idcycle: number) => Promise<WatanaBatchSummary[] | null>;
  cancelBatch: (batchCode: string) => Promise<boolean>;
  onBatchCancelled: () => void;
  loading?: boolean;
};

const COLORS = {
  title: "#0A229C",
  tableHead: "#014DDF",
  buttonBorder: "rgba(1, 77, 223, 0.42)",
  border: "#E4E9F2",
};

const secondaryButtonSx = {
  border: `solid 1px ${COLORS.buttonBorder}`,
  color: COLORS.title,
  textTransform: "none" as const,
  fontWeight: 600,
  "&:hover": {
    bgcolor: "rgba(1, 77, 223, 0.06)",
    borderColor: "#014DDF",
  },
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("es-PE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

type BatchTableColumn = {
  key: string;
  label: string;
  width?: number;
  align?: "left" | "center" | "right";
};

const BATCH_TABLE_COLUMNS: BatchTableColumn[] = [
  { key: "options", label: "", width: 44, align: "center" },
  { key: "code", label: "Código", width: 288 },
  { key: "status", label: "Estado", width: 138 },
  { key: "certificates", label: "Certificados", width: 104, align: "center" },
  { key: "requested", label: "Solicitado", width: 200 },
];

export const WatanaBatchesDrawer = ({
  open,
  onClose,
  idcycle,
  fetchBatches,
  cancelBatch,
  onBatchCancelled,
  loading = false,
}: WatanaBatchesDrawerProps) => {
  const [batches, setBatches] = useState<WatanaBatchSummary[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batchToCancel, setBatchToCancel] = useState<WatanaBatchSummary | null>(
    null
  );
  const [cancelling, setCancelling] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadBatches = useCallback(async () => {
    if (!idcycle) return;
    setLoadingBatches(true);
    const data = await fetchBatches(idcycle);
    setBatches(Array.isArray(data) ? data : []);
    setPage(0);
    setLoadingBatches(false);
  }, [fetchBatches, idcycle]);

  useEffect(() => {
    if (open && idcycle) {
      loadBatches();
    }
  }, [open, idcycle, loadBatches]);

  const paginatedBatches = useMemo(() => {
    const start = page * rowsPerPage;
    return batches.slice(start, start + rowsPerPage);
  }, [batches, page, rowsPerPage]);

  const onConfirmCancel = async () => {
    if (!batchToCancel) return;
    setCancelling(true);
    const ok = await cancelBatch(batchToCancel.batch_code);
    setCancelling(false);
    if (ok) {
      setBatchToCancel(null);
      await loadBatches();
      onBatchCancelled();
      CustomSnackbar("success", "Lote cancelado correctamente");
    }
  };

  const cancelModalText = batchToCancel?.has_sent_items
    ? "Este lote ya fue enviado a Watana. Se cancelará en Focus y los certificados quedarán disponibles para un nuevo lote. Watana puede seguir mostrando la carpeta enviada."
    : "Se cancelará el lote y los certificados quedarán disponibles para generar un nuevo lote.";

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "min(861px, 97vw)" },
            height: "100%",
            maxWidth: "100vw",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
          }}
        >
          {loadingBatches && <CustomBackdrop open={true} />}

          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: `1px solid ${COLORS.border}`,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    textTransform: "uppercase",
                    fontSize: 14,
                    fontWeight: 600,
                    color: COLORS.title,
                    lineHeight: 1.3,
                  }}
                >
                  Lotes de firma Watana
                </Typography>
              </Box>

              <Chip
                size="small"
                label={`${batches.length} ${
                  batches.length === 1 ? "lote registrado" : "lotes registrados"
                }`}
                sx={{
                  bgcolor: "#f0f6ff",
                  color: COLORS.title,
                  fontWeight: 600,
                  border: `1px solid ${COLORS.buttonBorder}`,
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon fontSize="small" />}
                  onClick={loadBatches}
                  disabled={loadingBatches || loading}
                  sx={secondaryButtonSx}
                >
                  Actualizar
                </Button>
                <IconButton onClick={onClose} size="small" aria-label="Cerrar">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {!loadingBatches && batches.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  px: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No hay lotes registrados para este ciclo.
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer
                  sx={{
                    flex: 1,
                    overflowX: "auto",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
                    <colgroup>
                      {BATCH_TABLE_COLUMNS.map((col) => (
                        <col
                          key={col.key}
                          style={col.width ? { width: col.width } : undefined}
                        />
                      ))}
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        {BATCH_TABLE_COLUMNS.map((col) => (
                          <TableCell
                            key={col.key}
                            align={col.align || "left"}
                            sx={{
                              background: COLORS.tableHead,
                              color: "#fff",
                              fontSize: 13,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              ...(col.key === "options" && { px: 0.5 }),
                            }}
                          >
                            {col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedBatches.map((batch) => {
                        const chipProps = getWatanaBatchStatus(
                          batch.status,
                          batch.status_label
                        );
                        const cancelTooltip = batch.can_cancel
                          ? "Cancelar lote"
                          : batch.cancel_block_reason || "No se puede cancelar";

                        return (
                          <TableRow key={batch.batch_id} hover>
                            <TableCell
                              align="center"
                              sx={{ width: 44, maxWidth: 44, p: "4px 0" }}
                            >
                              <Tooltip title={cancelTooltip}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={!batch.can_cancel}
                                    onClick={() =>
                                      batch.can_cancel && setBatchToCancel(batch)
                                    }
                                    aria-label="Cancelar lote"
                                    sx={{ p: 0.5 }}
                                  >
                                    <CancelOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: 13,
                                minWidth: 0,
                                overflow: "hidden",
                              }}
                            >
                              <Tooltip title={batch.batch_code} placement="top-start">
                                <Typography
                                  noWrap
                                  sx={{ fontSize: 13, color: COLORS.title }}
                                >
                                  {batch.batch_code}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                              <Chip
                                size="small"
                                label={chipProps.label}
                                color={chipProps.color}
                                variant={chipProps.variant || "filled"}
                                sx={{ maxWidth: "100%" }}
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontSize: 13, whiteSpace: "nowrap" }}
                            >
                              {batch.processed_certificates}/{batch.total_certificates}
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                              {formatDate(batch.requested_at)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {batches.length > 0 && (
                  <TablePagination
                    component="div"
                    count={batches.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25]}
                    labelRowsPerPage="Filas:"
                    sx={{ flexShrink: 0, mt: 1 }}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Drawer>

      {batchToCancel && (
        <ModalConfirm
          open={Boolean(batchToCancel)}
          title={`¿Cancelar lote ${batchToCancel.batch_code}?`}
          text={cancelModalText}
          closeModal={() => setBatchToCancel(null)}
          onCancel={() => setBatchToCancel(null)}
          onConfirm={onConfirmCancel}
          status2={cancelling}
        />
      )}
    </>
  );
};
